import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useParams, useSearchParams } from 'react-router-dom'
import { valuePropsApi } from '../../api/valueProps'
import { RefusedError } from '../../api/client'
import { useSpoke } from '../../hooks/useSpoke'
import { formatDate, formatMoney } from '../../lib/format'
import type { ApproachOption, OpeningReason, PropositionView, ThreadView, ValuePropositionTurn } from '../../types/valueProps'
import { reasonWord } from '../Expand'
import './value-proposition.css'

// The value proposition workspace: one "could sell next" opening, worked into a proposition WITH the
// model. Left, the mail with the account; centre, the draft and the conversation that shapes it (the
// approach, the person it is aimed at, the model's critique, the person's own edits); right, the
// meetings with the account and their transcripts, and the methodology's tactics that fit the approach.
// Every human edit is remembered as a correction the next draft learns from.

const TURN_WORDS: Record<string, string> = {
  'model-draft': 'Model draft', 'model-critique': 'Model critique', 'model-revise': 'Model revision', 'human-edit': 'Your edit', 'human-ask': 'You asked', accepted: 'Accepted',
}
const STAGE_WORDS: Record<string, string> = {
  lead: 'Lead', opportunity: 'Opportunity', proposal: 'Proposal out', negotiation: 'Negotiating', won: 'Won', lost: 'Lost', delivery: 'In delivery', dormant: 'Gone quiet', none: '—',
}

function errorText(e: unknown) { return e instanceof RefusedError ? e.refusal.message : e instanceof Error ? e.message : String(e) }
function time(iso: string) { const d = new Date(iso); return isNaN(d.getTime()) ? '' : d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) }

export function ValuePropositionWorkspace() {
  const spoke = useSpoke()
  const { companyId = '', line = '' } = useParams()
  const [params, setParams] = useSearchParams()
  const qc = useQueryClient()
  const vpId = params.get('vp')
  const [approach, setApproach] = useState<string | undefined>(params.get('approach') ?? undefined)
  const [target, setTarget] = useState<string | undefined>(params.get('target') ?? undefined)

  const ws = useQuery({
    queryKey: ['vp-workspace', spoke, companyId, line, approach ?? '', target ?? ''],
    queryFn: () => valuePropsApi.workspace(spoke, companyId, line, approach, target),
    staleTime: 30_000,
  })
  const vp = useQuery({ queryKey: ['vp', spoke, vpId], queryFn: () => valuePropsApi.get(spoke, vpId!), enabled: !!vpId, staleTime: 5_000 })

  const [text, setText] = useState('')
  const [ask, setAsk] = useState('')
  const draft = vp.data?.proposition.draft
  useEffect(() => { if (draft !== undefined) setText(draft) }, [draft])

  const land = (v: PropositionView) => {
    qc.setQueryData(['vp', spoke, v.proposition.valuePropositionId], v)
    setParams((p) => { p.set('vp', v.proposition.valuePropositionId); return p }, { replace: true })
    qc.invalidateQueries({ queryKey: ['vp-workspace'] })
  }
  const create = useMutation({
    mutationFn: () => valuePropsApi.create(spoke, { companyId, serviceLine: line, approach: ws.data?.approach, targetPersonId: ws.data?.target?.personId }),
    onSuccess: land,
  })
  const refine = useMutation({
    mutationFn: (body: { ask?: string; edit?: string; approach?: string; targetPersonId?: string }) => valuePropsApi.refine(spoke, vpId!, body),
    onSuccess: (v) => { land(v); setAsk('') },
  })
  const accept = useMutation({ mutationFn: () => valuePropsApi.accept(spoke, vpId!), onSuccess: land })

  const chooseApproach = (o: ApproachOption) => {
    if (!o.available) return
    setApproach(o.approach)
    setTarget(o.targetPersonId ?? undefined)
    setParams((p) => { p.set('approach', o.approach); if (o.targetPersonId) p.set('target', o.targetPersonId); else p.delete('target'); return p }, { replace: true })
    if (vpId && vp.data?.proposition.status !== 'accepted') refine.mutate({ approach: o.approach, targetPersonId: o.targetPersonId ?? '' })
  }
  const chooseTarget = (personId: string) => {
    setTarget(personId || undefined)
    setParams((p) => { if (personId) p.set('target', personId); else p.delete('target'); return p }, { replace: true })
    if (vpId && vp.data?.proposition.status !== 'accepted') refine.mutate({ targetPersonId: personId })
  }

  const data = ws.data
  const prop = vp.data?.proposition
  const turns = vp.data?.turns ?? []
  const accepted = prop?.status === 'accepted'
  const dirty = prop !== undefined && text.trim() !== prop.draft.trim()
  const critique = useMemo(() => [...turns].reverse().find((t) => t.kind === 'model-critique'), [turns])
  const external = useMemo(() => (data?.contacts ?? []).filter((c) => c.person.role !== 'colleague'), [data])
  const chosen = data?.options.find((o) => o.approach === data.approach)
  const fitTactics = useMemo(() => {
    if (!data) return []
    const ids = new Set(chosen?.tacticIds ?? [])
    const fit = data.tactics.filter((t) => ids.has(t.tacticId))
    return fit.length > 0 ? fit : data.tactics.slice(0, 3)
  }, [data, chosen])
  const glance = useMemo(() => {
    if (!data) return null
    const reasons = data.opening.reasons.filter((r) => r.kind !== 'no-route')
    const dm = data.contacts.find((c) => c.person.persona === 'decision-maker' && c.person.role !== 'colleague')
    const lastTouch = data.threads[0]?.lastAt ?? null
    return { reasons: reasons.length, people: external.length, dm: dm?.person.name ?? null, lastTouch, meetings: data.meetings.length, existing: data.existing.length }
  }, [data, external])

  if (ws.isPending) return <p className="muted centered">Loading…</p>
  if (ws.isError || !data) return <section className="page"><div className="refusal"><strong>Unavailable</strong><p>{ws.error ? errorText(ws.error) : 'No such account.'}</p><p><Link to="/expand">Back to Land &amp; Expand</Link></p></div></section>

  return (
    <section className="page value-prop">
      <header className="deals-head">
        <div>
          <p className="kicker"><Link to="/expand">Land &amp; Expand</Link> · <Link to={`/accounts/${encodeURIComponent(companyId)}`}>{data.company.name}</Link></p>
          <h1>{data.serviceLine} for {data.company.name}</h1>
          <p className="lede">
            {data.opening.reasons.filter((r) => r.kind !== 'no-route').slice(0, 3).map((r) => r.headline).join(' · ') || 'A line not yet sold at an account in delivery.'}
            {' '}{data.modelConfigured ? 'The model drafts from what is on file; you edit or ask; it revises until you accept.' : 'No model is configured here: the draft is the data, arranged for you to write over.'}
          </p>
        </div>
        <div className="actions">
          {!prop && <button className="button primary" onClick={() => create.mutate()} disabled={create.isPending}>{create.isPending ? 'Drafting…' : data.modelConfigured ? 'Draft with the model' : 'Start from the data'}</button>}
          {prop && !accepted && <button className="button primary" onClick={() => accept.mutate()} disabled={accept.isPending || dirty} title={dirty ? 'Save your edit first' : 'Keep this as the value proposition'}>{accept.isPending ? 'Saving…' : 'Accept this proposition'}</button>}
          {prop && accepted && <span className="tag stage won">Accepted {prop.acceptedAt ? formatDate(prop.acceptedAt) : ''}</span>}
          {prop && <button className="button" onClick={() => { setParams((p) => { p.delete('vp'); return p }); setText('') }}>New draft</button>}
        </div>
      </header>
      {(create.isError || refine.isError || accept.isError) && <p className="error">{errorText(create.error ?? refine.error ?? accept.error)}</p>}

      {glance && (
        <div className="deal-totals">
          <div className="stat accent"><strong>{glance.reasons}</strong><span className="muted">reasons it could sell</span></div>
          <div className="stat"><strong>{glance.people}</strong><span className="muted">people at the account</span></div>
          <div className="stat"><strong>{glance.dm ?? '—'}</strong><span className="muted">{glance.dm ? 'decision maker' : 'no decision maker on file'}</span></div>
          <div className="stat"><strong>{glance.lastTouch ? formatDate(glance.lastTouch) : '—'}</strong><span className="muted">last mail with them</span></div>
          <div className="stat"><strong>{glance.meetings}</strong><span className="muted">meetings on file</span></div>
          <div className="stat"><strong>{prop ? `rev ${prop.revision}` : glance.existing}</strong><span className="muted">{prop ? (prop.collaborative ? 'written together' : 'first draft') : 'earlier propositions'}</span></div>
        </div>
      )}

      <div className="vp-grid">
        <aside className="vp-col vp-mail">
          <h2 className="plan-h">Mail with {data.company.name}</h2>
          {data.threads.length === 0 ? <p className="muted small">No threads with this account in your mailbox.</p> : (
            <ul className="plain vp-threads">{data.threads.map((t) => <Thread key={t.key} t={t} />)}</ul>
          )}
          <h2 className="plan-h">Pursuits</h2>
          <ul className="plain vp-pursuits">
            {data.engagements.slice(0, 8).map((e) => (
              <li key={e.engagementId}>
                <span className={`tag stage ${e.stage}`}>{STAGE_WORDS[e.stage] ?? e.stage}</span>
                <strong>{e.subject}</strong>
                <span className="muted small">{[e.serviceLine, e.amount ? formatMoney(e.amount) : null, e.lastActivityOn ? formatDate(e.lastActivityOn) : null].filter(Boolean).join(' · ')}</span>
              </li>
            ))}
          </ul>
        </aside>

        <main className="vp-col vp-draft">
          <h2 className="plan-h">Why now</h2>
          <ul className="plain callouts">
            {data.opening.reasons.map((r) => <Reason key={r.kind + r.headline} r={r} />)}
          </ul>

          <h2 className="plan-h">How to take it in</h2>
          <div className="options">
            {data.options.map((o) => (
              <button key={o.approach} className={`option ${o.approach === data.approach ? 'on' : ''} ${o.available ? '' : 'off'}`} onClick={() => chooseApproach(o)} disabled={!o.available || refine.isPending || accepted} title={o.available ? '' : 'Nobody on file for this route yet'}>
                <span className="option-label"><strong>{o.label}</strong>{o.targetName && <span className="muted small">to {o.targetName}</span>}</span>
                <span className="option-cols">
                  <ul className="pros">{o.pros.map((p) => <li key={p}>{p}</li>)}</ul>
                  <ul className="cons">{o.cons.map((c) => <li key={c}>{c}</li>)}</ul>
                </span>
              </button>
            ))}
          </div>
          <label className="vp-target">
            <span className="muted small">Aimed at</span>
            <select value={target ?? data.target?.personId ?? ''} onChange={(e) => chooseTarget(e.target.value)} disabled={accepted}>
              <option value="">the account's leadership</option>
              {external.map((c) => <option key={c.person.personId} value={c.person.personId}>{c.person.name}{c.person.title ? `, ${c.person.title}` : ''}{c.person.persona ? ` — ${c.person.persona}` : ''}</option>)}
            </select>
          </label>

          <h2 className="plan-h">The proposition {prop && <span className="muted small">· revision {prop.revision}{vp.data?.modelWritten ? ' · model-written' : ''}</span>}</h2>
          {!prop && data.existing.length > 0 && (
            <div className="card">
              <p className="kicker">Earlier propositions at this account</p>
              <ul className="plain">{data.existing.map((x) => <li key={x.valuePropositionId}><button className="link" onClick={() => setParams((p) => { p.set('vp', x.valuePropositionId); return p })}>{x.serviceLine} · rev {x.revision} · {x.status}{x.collaborative ? ' · written together' : ''} · {formatDate(x.updatedAt)}</button></li>)}</ul>
            </div>
          )}
          {!prop && <div className="card vp-empty"><p className="muted">Press <strong>{data.modelConfigured ? 'Draft with the model' : 'Start from the data'}</strong> to open the first draft. It will be grounded in the mail, the pursuits and the people on file — nothing invented.</p></div>}
          {prop && (
            <>
              <textarea className="vp-text" value={text} onChange={(e) => setText(e.target.value)} readOnly={accepted} rows={16} spellCheck />
              {!accepted && (
                <div className="vp-edit-bar">
                  <button className="button" onClick={() => refine.mutate({ edit: text })} disabled={!dirty || refine.isPending}>{refine.isPending ? 'Saving…' : 'Save my edit'}</button>
                  {dirty && <span className="muted small">Your wording replaces the draft and is remembered for the next one.</span>}
                </div>
              )}
              {critique && <div className="callout critique"><p className="kicker">The model on its last revision</p><p>{critique.text}</p></div>}
              {!accepted && (
                <form className="vp-ask" onSubmit={(e) => { e.preventDefault(); if (ask.trim()) refine.mutate({ ask }) }}>
                  <input value={ask} onChange={(e) => setAsk(e.target.value)} placeholder={data.modelConfigured ? 'Ask for a change: shorter, lead with the CFO seat, less formal, add the next step…' : 'No model configured — edit the text directly'} disabled={!data.modelConfigured || refine.isPending} />
                  <button className="button primary" type="submit" disabled={!data.modelConfigured || !ask.trim() || refine.isPending}>{refine.isPending ? 'Revising…' : 'Ask the model'}</button>
                </form>
              )}
              <h2 className="plan-h">How it was written</h2>
              <ol className="turns">{turns.map((t) => <Turn key={t.turnId} t={t} />)}</ol>
            </>
          )}
        </main>

        <aside className="vp-col vp-calendar">
          <h2 className="plan-h">Meetings with {data.company.name}</h2>
          {data.meetings.length === 0 ? <p className="muted small">No meetings with this account on your calendar.</p> : (
            <ul className="plain vp-meetings">
              {data.meetings.map((m) => (
                <li key={m.meeting.meetingId}>
                  <strong>{m.meeting.subject}</strong>
                  <span className="muted small">{formatDate(m.meeting.start)} {time(m.meeting.start)}{m.contacts.length > 0 ? ` · ${m.contacts.map((p) => p.name).join(', ')}` : ''}</span>
                  {m.transcriptId ? <Link className="tag sig watch" to={`/transcripts/${encodeURIComponent(m.transcriptId)}`}>transcript</Link> : <span className="tag">no transcript</span>}
                </li>
              ))}
            </ul>
          )}
          <h2 className="plan-h">Tactics that fit</h2>
          {fitTactics.length === 0 ? <p className="muted small">No methodology loaded.</p> : (
            <ul className="plain vp-tactics">
              {fitTactics.map((t) => <li key={t.tacticId}><strong>{t.name}</strong><span className="small">{t.howTo || t.summary}</span></li>)}
            </ul>
          )}
          <h2 className="plan-h">Signals</h2>
          <ul className="plain signals">
            {data.signals.slice(0, 8).map((s) => <li key={s.signalId}><span className={`tag sig ${s.severity}`}>{s.type}</span> <span>{s.headline}</span><span className="muted small"> · {formatDate(s.occurredOn)}</span></li>)}
          </ul>
        </aside>
      </div>
    </section>
  )
}

function Reason({ r }: { r: OpeningReason }) {
  const to = r.personId ? `/contacts?open=${encodeURIComponent(r.personId)}` : r.engagementId ? `/opportunities` : r.tacticId ? '/playbook' : null
  return (
    <li className={`callout ${r.kind}`}>
      <span className="callout-kind">{reasonWord(r.kind)}</span>
      <span className="callout-text"><strong>{r.headline}</strong>{r.evidence && <span className="muted small">{r.evidence}</span>}</span>
      {to && <Link className="small" to={to}>open</Link>}
    </li>
  )
}

function Thread({ t }: { t: ThreadView }) {
  const [open, setOpen] = useState(false)
  return (
    <li className={open ? 'open' : ''}>
      <button className="thread-head" onClick={() => setOpen(!open)}>
        <strong>{t.subject}</strong>
        <span className="muted small">{formatDate(t.lastAt)} · {t.messages} message{t.messages === 1 ? '' : 's'}</span>
      </button>
      {open && (
        <ul className="plain thread-messages">
          {t.latest.map((m) => (
            <li key={m.id}>
              <span className="muted small">{formatDate(m.sentAt)} {time(m.sentAt)} · to {m.recipients.slice(0, 3).join(', ')}{m.recipients.length > 3 ? ` +${m.recipients.length - 3}` : ''}</span>
              <pre className="mail-text">{m.text || '(no text kept)'}</pre>
            </li>
          ))}
        </ul>
      )}
    </li>
  )
}

function Turn({ t }: { t: ValuePropositionTurn }) {
  const [open, setOpen] = useState(false)
  const human = t.kind.startsWith('human') || t.kind === 'accepted'
  return (
    <li className={`turn ${human ? 'human' : 'model'} ${t.kind}`}>
      <button className="turn-head" onClick={() => setOpen(!open)}>
        <span className="turn-kind">{TURN_WORDS[t.kind] ?? t.kind}</span>
        <span className="muted small">rev {t.revision} · {formatDate(t.at)} {time(t.at)}</span>
      </button>
      {t.kind === 'human-ask' && <p className="turn-ask">“{t.text}”</p>}
      {t.note && t.kind !== 'human-edit' && <p className="muted small">{t.note}</p>}
      {open && t.kind !== 'human-ask' && (
        <div className="turn-body">
          {t.kind === 'human-edit' && t.note && <><p className="kicker">Before</p><pre className="mail-text before">{t.note}</pre><p className="kicker">After (yours)</p></>}
          <pre className="mail-text">{t.text}</pre>
        </div>
      )}
    </li>
  )
}
